/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { FormGroup } from 'react-bootstrap'
import { Formik, yupToFormErrors } from 'formik'
import Loader from '../../../components/shared/Loader'
import { useServiceContext } from '../../../root'
import ErrorMessage from '../../../components/shared/ErrorMessage'
import { Select } from '../../../components/Select'
import ValidationSchema from './EditFormValidation'
import { useTranslation } from 'react-i18next'
import { toast } from '../../../services/toast'
import Modal from '../../../components/Modal'
import { DatePickerWrapper } from '../../../components/DatePicker'
import DateTimePicker from '~/components/DatePicker/DateTimePicker'
import { useAssembledUsersGroups, useSendNotification } from '../../../hooks/useNotifications'
import _ from 'lodash'
import { getDatePickerFormat, getDateTimeFnsParseFormats } from '~/utils/dateFormatting'

interface ComposeNotificationProps {
  onClose: () => void
}

interface NotificationValues {
  notification: {
    merged: any[]
    mergedAllData?: any[]
    subject: string
    body: string
    expiration: Date | null
    setExpiration: boolean
  }
}

const ComposeNotification: React.FC<ComposeNotificationProps> = ({ onClose }) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const { data: usersGroupsData, isLoading } = useAssembledUsersGroups()
  const { mutate: sendNotification, isPending: isSending } = useSendNotification()

  const roleOptions = usersGroupsData?.merged.map((r: any) => ({ value: r._uid, label: r.chName, additional: r })) || []

  const doClose = () => {
    if (_.isFunction(onClose)) {
      onClose()
    }
  }

  const save = (values: NotificationValues) => {
    const subject = values.notification.subject.replace(/<\/?[^>]+(>|$)/g, '')
    let body = values.notification.body.replace(/<\/?[^>]+(>|$)/g, '')
    body = body.replace(/\n/g, '<br />')

    const payload: any = {
      to: { merged: values.notification?.mergedAllData || [] },
      subject,
      body
    }

    // Only include expiration date if user has checked the checkbox AND selected a valid date
    if (values.notification.setExpiration && values.notification.expiration) {
      payload.expiration = new Date(values.notification.expiration)
    }

    sendNotification(payload, {
      onSuccess: () => {
        doClose()
        window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
        toast.success(translate('NOTIFICATIONS_CREATE_SUCCESS_MSG'))
      },
      onError: (error: any) => {
        toast.error(translate('NOTIFICATIONS_CREATE_FAIL_MSG', { error: error?.data?.message }))
      }
    })
  }

  return (
    <Modal show={true} handleClose={doClose} title={translate('NOTIFICATIONS_COMPOSE_NOTIFICATION_H_TITLE')} size="lg">
      <Formik<NotificationValues>
        initialValues={{
          notification: {
            merged: [],
            subject: '',
            body: '',
            expiration: null,
            setExpiration: false
          }
        }}
        enableReinitialize
        validate={(values) => {
          return ValidationSchema()
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        onSubmit={(values) => {
          save(values)
        }}
      >
        {({ values, setFieldValue, handleSubmit, handleChange }) => (
          <>
            <Modal.Body>
              <Loader blocking={isLoading || isSending}>
                <div style={{ minHeight: '200px', position: 'relative' }}>
                  <FormGroup controlId="4033648f-3b5a-4599-aaf8-966b8851a93a">
                    <label className="control-label">{translate('NOTIFICATIONS_DETAIL_TH_TO')}</label>
                    <Select
                      placeholder={translate('SELECT_USERS/GROUPS...')}
                      name="notification.merged"
                      onChange={(option: any) => {
                        setFieldValue('notification.merged', option ? option.map((o: any) => o.value) : [])
                        setFieldValue('notification.mergedAllData', option ? option.map((o: any) => o.additional) : [])
                      }}
                      multi={true}
                      value={values?.notification?.merged}
                      options={roleOptions}
                      showNewOptionAtTop={true}
                      searchable
                      id="4033648f-3b5a-4599-aaf8-966b8851a93a"
                    />
                    <ErrorMessage name="notification.merged" />
                  </FormGroup>
                  <FormGroup controlId="e419ef94-9631-450c-8b32-725281160194" className="w100p">
                    <label className="control-label">{translate('NOTIFICATIONS_DETAIL_TH_SUBJECT')}</label>
                    <input
                      type="text"
                      className="form-control"
                      name="notification.subject"
                      maxLength={500}
                      value={values?.notification?.subject || ''}
                      onChange={handleChange}
                      id="e419ef94-9631-450c-8b32-725281160194"
                    />
                    <ErrorMessage name="notification.subject" />
                  </FormGroup>
                  <FormGroup controlId="79f1f37b-1d13-4bf5-8eb6-c51028f6fc39">
                    <label className="control-label">{translate('NOTIFICATIONS_DETAIL_TH_BODY')}</label>
                    <FormGroup className="wparag" controlId="cb52d94c-770c-4d7e-8181-b26a1a320b26">
                      <textarea
                        className="form-control"
                        maxLength={1000}
                        name="notification.body"
                        onChange={(e: any) => {
                          setFieldValue('notification.body', e.target.value)
                        }}
                        id="cb52d94c-770c-4d7e-8181-b26a1a320b26"
                        value={values?.notification?.body ? values?.notification?.body : ''}
                        rows={6}
                      />
                      <div className="text-muted small text-right">
                        {values?.notification?.body ? values?.notification?.body.length : 0}/1000{' '}
                        {translate('CHAR_COUNT_LIMIT')}
                      </div>
                    </FormGroup>
                    <ErrorMessage name="notification.body" />
                  </FormGroup>
                  <FormGroup controlId="d3ab2b12-1a55-45cc-8580-f4db1afec5d3">
                    <label className="control-label">
                      <input
                        type="checkbox"
                        name="notification.setExpiration"
                        checked={values?.notification?.setExpiration}
                        onChange={(e) => setFieldValue('notification.setExpiration', e.target.checked)}
                        id="d3ab2b12-1a55-45cc-8580-f4db1afec5d3"
                      />{' '}
                      {translate('NOTIFICATIONS_COMPOSE_NOTIFICATION_SET_EXPIRATION')}
                    </label>
                  </FormGroup>
                  <FormGroup
                    controlId="39b84a8e-480c-4023-8559-cbebc3979dc1"
                    style={{ display: values?.notification?.setExpiration ? 'block' : 'none' }}
                  >
                    <DatePickerWrapper
                      alwaysEditable={true}
                      value={() => {
                        if (!values?.notification?.expiration) return null
                        const d = new Date(values.notification.expiration)
                        const year = d.getFullYear()
                        const month = String(d.getMonth() + 1).padStart(2, '0')
                        const day = String(d.getDate()).padStart(2, '0')
                        const hours = d.getHours()
                        const minutes = String(d.getMinutes()).padStart(2, '0')
                        const seconds = String(d.getSeconds()).padStart(2, '0')
                        const ampm = hours >= 12 ? 'PM' : 'AM'
                        const displayHours = hours % 12 || 12
                        return `${year}${month}${day} ${String(displayHours).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`
                      }}
                      input={
                        <DateTimePicker
                          name="notification.expiration"
                          selected={values?.notification?.expiration ?? null}
                          placeholderText={translate('-SELECT_DATE-')}
                          onChange={(date: Date | null) => setFieldValue('notification.expiration', date)}
                          popoverTargetOffset="3px 0"
                          dateFormatCalendar="MMMM"
                          showYearDropdown
                          className="form-control field-control pb10 w100p"
                          id="notification.expiration"
                          showTimeInput
                          timeInputLabel={translate('TIME_LABEL_CLINICAL_ACTIVITY_REPORT')}
                          autoComplete="off"
                          wrapperClassName="w100p"
                          popperPlacement="bottom-start"
                          showPopperArrow={false}
                        />
                      }
                    />
                    <ErrorMessage name="notification.expiration" />
                  </FormGroup>
                </div>
              </Loader>
            </Modal.Body>
            <Modal.Footer>
              <Modal.Footer.Button variant="secondary" onClick={doClose}>
                <span data-action="cancel" id="c959c9af-d4b0-4ca1-82c4-5b24b040f3dc">
                  {translate('CANCEL')}
                </span>
              </Modal.Footer.Button>
              <Modal.Footer.Button variant="primary" onClick={() => handleSubmit()}>
                <span data-action="save" id="c95wdcwc9af-d4b0-4ca1-82c4-5b24b040f3dc" className="text-capitalize">
                  {translate('BTN_SEND')}
                </span>
              </Modal.Footer.Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default ComposeNotification
